import React from 'react';
import PropTypes from 'prop-types';

import Select from '../../../../components/Select';
import { Button, BUTTON_TYPE_WHITE } from '../../../../components/Buttons/Button';
import Strings from '../../../../Strings';
import accessStyles from '../AccessSimulator/AccessSimulator.module.scss';
import { DraftField, toIntOrNull, toTextOrNull } from './fields';
import styles from './AthenaSimulator.module.scss';

const NUMBER_FIELDS = [
  ['practice_id', 'athenaSimPractice'],
  ['athena_patient_id', 'athenaSimAthenaId'],
  ['department_id', 'athenaSimDepartment'],
];

const TEXT_FIELDS = [
  ['first_name', 'firstName'],
  ['last_name', 'lastName'],
  ['mbi', 'athenaSimMbi'],
  ['email', 'athenaSimEmail'],
  ['mobile_phone', 'athenaSimMobilePhone'],
  ['home_phone', 'athenaSimHomePhone'],
  ['address1', 'athenaSimAddress'],
  ['address2', 'athenaSimAddress2'],
  ['city', 'athenaSimCity'],
  ['state', 'athenaSimState'],
  ['zip', 'athenaSimZip'],
  ['country_code', 'athenaSimCountry'],
];

const SEX_OPTIONS = [
  { value: null, label: '-' },
  { value: 'F', label: 'F' },
  { value: 'M', label: 'M' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: Strings.athenaSimProblemActive },
  { value: 'resolved', label: Strings.athenaSimProblemResolved },
];

const emptyProblem = () => ({
  problem_id: null, name: '', icd10_code: '', snomed_code: '', status: 'active', onset_date: '', resolved_date: '',
});

// The draft keeps every field as the text the tester typed; `extra` is edited
// as JSON text.
export const emptyPatientDraft = practiceId => ({
  ...Object.fromEntries([...NUMBER_FIELDS, ...TEXT_FIELDS].map(([field]) => [field, ''])),
  practice_id: String(practiceId),
  dob: '',
  sex: null,
  primary_provider_id: null,
  problems: [],
  extra: '{}',
});

export const patientDraftFrom = patient => ({
  ...emptyPatientDraft(''),
  ...Object.fromEntries([...NUMBER_FIELDS, ...TEXT_FIELDS].map(([field]) => [field, patient[field] ?? ''])),
  dob: patient.dob,
  sex: patient.sex ?? null,
  primary_provider_id: patient.primary_provider_id ?? null,
  problems: patient.problems.map(problem => ({ ...emptyProblem(), ...problem })),
  extra: JSON.stringify(patient.extra || {}, null, 2),
});

const parseExtra = text => {
  try {
    const value = JSON.parse(text || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : undefined;
  } catch (error) {
    return undefined;
  }
};

// Mirrors the backend validation so Save cannot produce a 400.
export const patientDraftIsValid = draft => (
  !!draft.first_name.trim() && !!draft.last_name.trim() && /^\d{4}-\d{2}-\d{2}$/.test(draft.dob)
  && Number(draft.practice_id) > 0 && parseExtra(draft.extra) !== undefined
  && draft.problems.every(problem => problem.name.trim() && problem.icd10_code.trim())
);

export const patientFromDraft = draft => ({
  ...Object.fromEntries(TEXT_FIELDS.map(([field]) => [field, toTextOrNull(draft[field])])),
  ...Object.fromEntries(NUMBER_FIELDS.map(([field]) => [field, toIntOrNull(draft[field])])),
  first_name: draft.first_name.trim(),
  last_name: draft.last_name.trim(),
  dob: draft.dob,
  sex: draft.sex,
  primary_provider_id: draft.primary_provider_id,
  problems: draft.problems.map(problem => ({
    problem_id: problem.problem_id || 0,
    name: problem.name.trim(),
    icd10_code: problem.icd10_code.trim(),
    snomed_code: toTextOrNull(problem.snomed_code),
    status: problem.status,
    onset_date: toTextOrNull(problem.onset_date),
    resolved_date: problem.status === 'resolved' ? toTextOrNull(problem.resolved_date) : null,
  })),
  extra: parseExtra(draft.extra),
});

/**
 * Add/edit form of a simulated Athena patient: demographics, the MBI (served
 * as a Medicare insurance member id), the primary care provider, the problem
 * list and any further raw Athena fields.
 */
function PatientModal({ draft, setDraft, providers, isSaving }) {
  const setField = (field, value) => setDraft({ ...draft, [field]: value });
  const setProblem = (index, field, value) => setField(
    'problems',
    draft.problems.map((problem, i) => (i === index ? { ...problem, [field]: value } : problem)),
  );

  const pcpOptions = [
    { value: null, label: Strings.athenaSimNoPcp },
    ...providers
      .filter(provider => provider.kind === 'provider' && String(provider.practice_id) === String(draft.practice_id))
      .map(provider => ({ value: provider.provider_id, label: `${provider.provider_id} · ${provider.last_name}, ${provider.first_name}` })),
  ];

  return (
    <div className={accessStyles.patientModal}>
      <div className={accessStyles.patientModalIdentity}>
        {NUMBER_FIELDS.map(([field, label]) => (
          <DraftField key={field} id={`athena-sim-patient-${field}`} label={Strings[label]} type="number" value={draft[field]} onChange={value => setField(field, value)} disabled={isSaving} />
        ))}
        <DraftField id="athena-sim-patient-dob" label={Strings.athenaSimDob} type="date" value={draft.dob} onChange={value => setField('dob', value)} disabled={isSaving} />
        <div className={accessStyles.patientModalField}>
          {Strings.athenaSimSex}
          <Select id="athena-sim-patient-sex" data={SEX_OPTIONS} value={draft.sex} onChange={option => setField('sex', option?.value ?? null)} disabled={isSaving} />
        </div>
        <div className={accessStyles.patientModalField}>
          {Strings.athenaSimPcp}
          <Select
            id="athena-sim-patient-pcp"
            data={pcpOptions}
            value={draft.primary_provider_id}
            onChange={option => setField('primary_provider_id', option?.value ?? null)}
            disabled={isSaving}
          />
        </div>
        {TEXT_FIELDS.map(([field, label]) => (
          <DraftField key={field} id={`athena-sim-patient-${field}`} label={Strings[label]} value={draft[field]} onChange={value => setField(field, value)} disabled={isSaving} />
        ))}
      </div>

      <div className={accessStyles.patientModalScript}>
        <div className={accessStyles.patientModalScriptHeader}>
          <span className={accessStyles.patientModalOperation}>{Strings.athenaSimProblems}</span>
          <Button
            buttonType={BUTTON_TYPE_WHITE}
            onClick={() => setField('problems', [...draft.problems, emptyProblem()])}
            disabled={isSaving}
            data-testid="button[add-athena-sim-problem]"
          >
            {Strings.athenaSimAddProblem}
          </Button>
        </div>
        {draft.problems.map((problem, index) => (
          // Problems have no stable key before they are saved.
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className={styles.problemRow}>
            <DraftField id={`athena-sim-problem-${index}-name`} label={Strings.athenaSimProblemName} value={problem.name} onChange={value => setProblem(index, 'name', value)} disabled={isSaving} />
            <DraftField id={`athena-sim-problem-${index}-icd10`} label={Strings.athenaSimIcd10} value={problem.icd10_code} onChange={value => setProblem(index, 'icd10_code', value)} disabled={isSaving} />
            <DraftField id={`athena-sim-problem-${index}-snomed`} label={Strings.athenaSimSnomed} value={problem.snomed_code} onChange={value => setProblem(index, 'snomed_code', value)} disabled={isSaving} />
            <div className={accessStyles.patientModalField}>
              {Strings.athenaSimProblemStatus}
              <Select id={`athena-sim-problem-${index}-status`} data={STATUS_OPTIONS} value={problem.status} onChange={option => setProblem(index, 'status', option?.value || 'active')} disabled={isSaving} />
            </div>
            <DraftField id={`athena-sim-problem-${index}-onset`} label={Strings.athenaSimOnset} type="date" value={problem.onset_date} onChange={value => setProblem(index, 'onset_date', value)} disabled={isSaving} />
            <DraftField id={`athena-sim-problem-${index}-resolved`} label={Strings.athenaSimResolved} type="date" value={problem.resolved_date} onChange={value => setProblem(index, 'resolved_date', value)} disabled={isSaving || problem.status !== 'resolved'} />
            <Button
              buttonType={BUTTON_TYPE_WHITE}
              onClick={() => setField('problems', draft.problems.filter((_, i) => i !== index))}
              disabled={isSaving}
              data-testid="button[remove-athena-sim-problem]"
            >
              {Strings.athenaSimRemove}
            </Button>
          </div>
        ))}
      </div>

      <label className={accessStyles.scriptField} htmlFor="athena-sim-patient-extra">
        <span data-tooltip-id="tooltip" data-tooltip-content={Strings.athenaSimExtraTooltip}>{Strings.athenaSimExtra}</span>
        <textarea
          id="athena-sim-patient-extra"
          rows={4}
          value={draft.extra}
          onChange={event => setField('extra', event.target.value)}
          disabled={isSaving}
        />
      </label>
      {parseExtra(draft.extra) === undefined && <span className="validation-message">{Strings.athenaSimExtraInvalid}</span>}
      <p className={accessStyles.patientModalHint}>{Strings.athenaSimPatientHint}</p>
    </div>
  );
}

PatientModal.propTypes = {
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  providers: PropTypes.arrayOf(PropTypes.object).isRequired,
  isSaving: PropTypes.bool,
};

export default PatientModal;
